import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  User, Mail, Phone, Stethoscope, FileText, Lock, Save,
  Pencil, X, Calendar, Shield, Activity, ChevronRight, LogOut
} from 'lucide-react';
import PhoneInput from '../components/PhoneInput';
import { useNavigate } from 'react-router-dom';
import DoctorProfileSettings from '../components/DoctorProfileSettings';

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isPatient = user?.role === 'USER';

  // Patient demographics
  const [demographics, setDemographics] = useState(null);
  const [editingDemo, setEditingDemo] = useState(false);
  const [demoForm, setDemoForm] = useState({});
  const [savingDemo, setSavingDemo] = useState(false);

  // Patient baseline vitals
  const [vitals, setVitals] = useState(null);
  const [editingVitals, setEditingVitals] = useState(false);
  const [vitalsForm, setVitalsForm] = useState({ bloodPressureSystolic: '', bloodPressureDiastolic: '', heartRate: '', weight: '' });
  const [savingVitals, setSavingVitals] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [profile, setProfile] = useState({
    name: '',
    email: '',
    role: '',
    specialization: '',
    bio: '',
    phone: '',
    createdAt: '',
  });

  const [editForm, setEditForm] = useState({});

  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => { loadProfile(); }, []);
  useEffect(() => { if (isPatient) loadDemographics(); }, [isPatient]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/profile');
      const userData = response.data.user;
      setProfile({
        name: userData.name || '',
        email: userData.email || '',
        role: userData.role || '',
        specialization: userData.specialization || '',
        bio: userData.bio || '',
        phone: userData.phone || '',
        createdAt: userData.createdAt || '',
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    setEditForm({ ...profile });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditForm({});
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/profile', {
        name: editForm.name,
        specialization: editForm.specialization,
        bio: editForm.bio,
        phone: editForm.phone,
      });
      setProfile({ ...profile, ...editForm });
      setEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await api.put('/profile/password', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      toast.success('Password changed successfully');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const loadDemographics = async () => {
    try {
      const res = await api.get('/patient-profile/me');
      if (res.data?.success) {
        const d = res.data.data?.demographics || {};
        setDemographics(d);
        setDemoForm({
          dateOfBirth: d.dateOfBirth ? d.dateOfBirth.toString().slice(0,10) : '',
          gender: d.gender || '',
          livesAlone: d.livesAlone ?? false,
          hasCaregiver: d.hasCaregiver ?? false,
          preferredLanguage: d.preferredLanguage || '',
        });
        const v = res.data.data?.baseline?.normalVitals || {};
        setVitals(v);
        setVitalsForm({
          bloodPressureSystolic: v.bloodPressureSystolic || '',
          bloodPressureDiastolic: v.bloodPressureDiastolic || '',
          heartRate: v.heartRate || '',
          weight: v.weight || '',
        });
      }
    } catch { /* non-fatal */ }
  };

  const saveVitals = async () => {
    setSavingVitals(true);
    try {
      const payload = {
        normalVitals: {
          bloodPressureSystolic: vitalsForm.bloodPressureSystolic ? Number(vitalsForm.bloodPressureSystolic) : undefined,
          bloodPressureDiastolic: vitalsForm.bloodPressureDiastolic ? Number(vitalsForm.bloodPressureDiastolic) : undefined,
          heartRate: vitalsForm.heartRate ? Number(vitalsForm.heartRate) : undefined,
          weight: vitalsForm.weight ? Number(vitalsForm.weight) : undefined,
          recordedAt: new Date(),
        }
      };
      await api.put('/patient-profile/me', { baseline: payload });
      setVitals(payload.normalVitals);
      setEditingVitals(false);
      toast.success('Baseline vitals saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save vitals');
    } finally {
      setSavingVitals(false);
    }
  };

  const saveDemographics = async () => {
    setSavingDemo(true);
    try {
      await api.put('/patient-profile/me', { demographics: demoForm });
      setDemographics(demoForm);
      setEditingDemo(false);
      toast.success('Demographics updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update demographics');
    } finally {
      setSavingDemo(false);
    }
  };

  const isDoctor = profile.role === 'DOCTOR';
  const isAdmin = profile.role === 'ADMIN';

  if (loading) {
    return (
      <div className="space-y-6 py-6">
        <div className="h-24 rounded-2xl animate-pulse bg-slate-100" />
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-2xl animate-pulse bg-slate-100" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">

      {/* ─── Profile Hero Card ─── */}
      <div className="bg-white rounded-2xl shadow-premium hover:shadow-premium-hover transition-all duration-500 border border-outline-variant/10 overflow-hidden relative">
        {/* Decorative gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-secondary/[0.03] pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-48 h-48 bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="p-8 relative z-10">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-extrabold ring-4 flex-shrink-0 ${
              isDoctor ? 'bg-primary ring-primary/10' : isAdmin ? 'bg-slate-900 ring-slate-900/10' : 'bg-secondary ring-secondary/10'
            }`}>
              {profile.name?.charAt(0)?.toUpperCase() || '?'}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">
                    {isDoctor ? 'Dr. ' : ''}{profile.name}
                  </h2>
                  <p className="text-sm text-slate-500 font-medium mt-0.5">{profile.email}</p>
                </div>
                {!editing && (
                  <button
                    onClick={startEditing}
                    className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-primary shadow-sm hover:shadow-primary/25 transition-all duration-300 active:scale-95 flex-shrink-0"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit Profile
                  </button>
                )}
              </div>

              {/* Badges */}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <span className={`text-xs font-extrabold px-3 py-1.5 rounded-full border uppercase ${
                  isDoctor ? 'bg-primary/10 text-primary border-primary/20' :
                  isAdmin ? 'bg-slate-100 text-slate-700 border-slate-200' :
                  'bg-secondary/10 text-secondary border-secondary/20'
                }`}>
                  {isDoctor ? 'Doctor' : isAdmin ? 'Administrator' : 'Patient'}
                </span>
                {profile.specialization && (
                  <span className="text-xs font-extrabold px-3 py-1.5 rounded-full border bg-violet-50 text-violet-600 border-violet-100 uppercase">
                    {profile.specialization}
                  </span>
                )}
              </div>

              {/* Bio */}
              {profile.bio && (
                <p className="text-sm text-slate-500 font-medium mt-4 leading-relaxed max-w-lg">
                  {profile.bio}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Info Cards ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { icon: Mail, label: 'Email', value: profile.email, color: '#00478d' },
          { icon: Phone, label: 'Phone', value: profile.phone || 'Not provided', color: '#00696b' },
          { icon: Calendar, label: 'Member Since', value: new Date(profile.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), color: '#5f4300' },
          ...(isDoctor ? [{ icon: Stethoscope, label: 'Specialization', value: profile.specialization || 'Not specified', color: '#7c3aed' }] : []),
          ...(profile.bio ? [{ icon: FileText, label: 'Bio', value: profile.bio, color: '#334155' }] : []),
        ].map((item, i) => (
          <div key={i} className="bg-white rounded-2xl shadow-premium hover:shadow-premium-hover transition-all duration-500 p-6 border border-outline-variant/10">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.color}12` }}>
                <item.icon className="w-5 h-5" style={{ color: item.color }} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em] mb-1">{item.label}</p>
                <p className="text-sm font-bold text-slate-900 break-words">{item.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── Patient Demographics ─── */}
      {isPatient && demographics !== null && (
        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Health Profile</p>
                <h3 className="font-headline text-xl font-bold text-slate-900 tracking-tight">Demographics</h3>
              </div>
            </div>
            {!editingDemo && (
              <button onClick={() => setEditingDemo(true)} className="flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/80 transition-colors">
                <Pencil className="w-4 h-4" /> Edit
              </button>
            )}
          </div>

          {!editingDemo ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'Date of Birth', value: demographics.dateOfBirth ? (() => { const [y,m,d] = demographics.dateOfBirth.toString().slice(0,10).split('-'); return `${m}/${d}/${y}`; })() : '—' },
                { label: 'Gender', value: demographics.gender ? demographics.gender.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—' },
                { label: 'Lives Alone', value: demographics.livesAlone ? 'Yes' : 'No' },
                { label: 'Has Caregiver', value: demographics.hasCaregiver ? 'Yes' : 'No' },
                { label: 'Preferred Language', value: demographics.preferredLanguage || '—' },
              ].map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3 border border-outline-variant/10">
                  <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-1">{item.label}</p>
                  <p className="text-sm font-bold text-slate-900">{item.value}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Date of Birth</label>
                  <input type="date" value={demoForm.dateOfBirth} onChange={e => setDemoForm(p => ({ ...p, dateOfBirth: e.target.value }))} className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
                </div>
                <div>
                  <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Gender</label>
                  <select value={demoForm.gender} onChange={e => setDemoForm(p => ({ ...p, gender: e.target.value }))} className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all">
                    <option value="">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non_binary">Non-binary</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Preferred Language</label>
                  <select value={demoForm.preferredLanguage} onChange={e => setDemoForm(p => ({ ...p, preferredLanguage: e.target.value }))} className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all">
                    <option value="">Select language</option>
                    {['English','French','Spanish','Arabic','Mandarin','Portuguese','Hindi','Other'].map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={demoForm.livesAlone} onChange={e => setDemoForm(p => ({ ...p, livesAlone: e.target.checked }))} className="w-4 h-4 rounded" />
                  <span className="font-medium text-slate-700">I live alone</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={demoForm.hasCaregiver} onChange={e => setDemoForm(p => ({ ...p, hasCaregiver: e.target.checked }))} className="w-4 h-4 rounded" />
                  <span className="font-medium text-slate-700">I have a caregiver</span>
                </label>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={saveDemographics} disabled={savingDemo} className="flex-1 flex items-center justify-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-primary transition-all disabled:opacity-50">
                  <Save className="w-4 h-4" />{savingDemo ? 'Saving...' : 'Save Demographics'}
                </button>
                <button type="button" onClick={() => setEditingDemo(false)} className="px-5 py-3 bg-slate-50 text-slate-600 rounded-xl font-bold text-sm border border-outline-variant/20 hover:bg-white transition-all">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Patient Baseline Vitals ─── */}
      {isPatient && vitals !== null && (
        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-secondary/10">
                <Activity className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Health Baseline</p>
                <h3 className="font-headline text-xl font-bold text-slate-900 tracking-tight">Normal Vitals</h3>
              </div>
            </div>
            {!editingVitals && (
              <button onClick={() => setEditingVitals(true)} className="flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/80 transition-colors">
                <Pencil className="w-4 h-4" /> {vitals.recordedAt ? 'Update' : 'Set Baseline'}
              </button>
            )}
          </div>

          {!editingVitals ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Systolic BP', value: vitals.bloodPressureSystolic ? `${vitals.bloodPressureSystolic} mmHg` : '—' },
                { label: 'Diastolic BP', value: vitals.bloodPressureDiastolic ? `${vitals.bloodPressureDiastolic} mmHg` : '—' },
                { label: 'Heart Rate', value: vitals.heartRate ? `${vitals.heartRate} bpm` : '—' },
                { label: 'Weight', value: vitals.weight ? `${vitals.weight} kg` : '—' },
              ].map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3 border border-outline-variant/10">
                  <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-1">{item.label}</p>
                  <p className="text-sm font-bold text-slate-900">{item.value}</p>
                </div>
              ))}
              {vitals.recordedAt && (
                <p className="col-span-2 sm:col-span-4 text-xs text-slate-400 mt-1">
                  Recorded {new Date(vitals.recordedAt).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-slate-500 font-medium">Enter your typical resting values. These are used to detect significant changes in your health.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Systolic BP (mmHg)</label>
                  <input type="number" min="60" max="250" placeholder="e.g. 120" value={vitalsForm.bloodPressureSystolic} onChange={e => setVitalsForm(p => ({ ...p, bloodPressureSystolic: e.target.value }))} className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
                </div>
                <div>
                  <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Diastolic BP (mmHg)</label>
                  <input type="number" min="40" max="150" placeholder="e.g. 80" value={vitalsForm.bloodPressureDiastolic} onChange={e => setVitalsForm(p => ({ ...p, bloodPressureDiastolic: e.target.value }))} className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
                </div>
                <div>
                  <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Resting Heart Rate (bpm)</label>
                  <input type="number" min="30" max="200" placeholder="e.g. 70" value={vitalsForm.heartRate} onChange={e => setVitalsForm(p => ({ ...p, heartRate: e.target.value }))} className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
                </div>
                <div>
                  <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Weight (kg)</label>
                  <input type="number" min="10" max="500" step="0.1" placeholder="e.g. 72" value={vitalsForm.weight} onChange={e => setVitalsForm(p => ({ ...p, weight: e.target.value }))} className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={saveVitals} disabled={savingVitals} className="flex-1 flex items-center justify-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-primary transition-all disabled:opacity-50">
                  <Save className="w-4 h-4" />{savingVitals ? 'Saving...' : 'Save Vitals'}
                </button>
                <button type="button" onClick={() => setEditingVitals(false)} className="px-5 py-3 bg-slate-50 text-slate-600 rounded-xl font-bold text-sm border border-outline-variant/20 hover:bg-white transition-all">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Edit Form (shown when editing) ─── */}
      {editing && (
        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.02] to-transparent pointer-events-none" />
          <form onSubmit={handleSave} className="p-8 relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
                  <Pencil className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Editing</p>
                  <h3 className="font-headline text-xl font-bold text-slate-900 tracking-tight">Edit Profile</h3>
                </div>
              </div>
              <button type="button" onClick={cancelEditing} className="p-2 hover:bg-slate-50 rounded-xl transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Full Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>

              <div>
                <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  disabled
                  className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-500 cursor-not-allowed"
                />
                <p className="text-xs text-slate-500 mt-1.5 font-medium">Email cannot be changed</p>
              </div>

              <div>
                <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Phone Number</label>
                <PhoneInput
                  value={editForm.phone || ''}
                  onChange={(val) => setEditForm({ ...editForm, phone: val })}
                />
              </div>

              {isDoctor && (
                <div>
                  <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Specialization</label>
                  <select
                    value={editForm.specialization || ''}
                    onChange={(e) => setEditForm({ ...editForm, specialization: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  >
                    <option value="">Select specialization</option>
                    {[
                      'General Practice','Family Medicine','Internal Medicine',
                      'Cardiology','Dermatology','Emergency Medicine',
                      'Endocrinology','Gastroenterology','Geriatrics',
                      'Hematology','Infectious Disease','Nephrology',
                      'Neurology','Obstetrics & Gynecology','Oncology',
                      'Ophthalmology','Orthopedics','Otolaryngology (ENT)',
                      'Pediatrics','Psychiatry','Pulmonology',
                      'Radiology','Rheumatology','Surgery (General)',
                      'Urology','Other',
                    ].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Bio</label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                  rows={3}
                  placeholder="Tell us about yourself..."
                  maxLength={500}
                />
                <p className="text-xs text-slate-500 mt-1 font-medium">{(editForm.bio || '').length}/500</p>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-primary shadow-sm hover:shadow-primary/25 transition-all duration-300 active:scale-95 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={cancelEditing}
                className="px-5 py-3 bg-slate-50 text-slate-600 rounded-xl font-bold text-sm border border-outline-variant/20 hover:bg-white hover:shadow-sm transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── Doctor Practice Settings ─── */}
      {isDoctor && <DoctorProfileSettings />}

      {/* ─── Logout ─── */}
      <button
        onClick={() => { logout(); }}
        className="w-full flex items-center justify-center gap-2 px-5 py-3 border border-red-200 text-red-500 rounded-2xl font-bold text-sm hover:bg-red-50 active:scale-95 transition-all"
      >
        <LogOut className="w-4 h-4" />
        Sign Out
      </button>

      {/* ─── Security Section ─── */}
      <div className="bg-white rounded-2xl shadow-premium hover:shadow-premium-hover transition-all duration-500 border border-outline-variant/10 overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-500/[0.02] to-transparent pointer-events-none" />
        <div className="p-8 relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100">
                <Shield className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Account</p>
                <h3 className="font-headline text-xl font-bold text-slate-900 tracking-tight">Security</h3>
              </div>
            </div>
            {!showPasswordForm && (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/80 transition-colors"
              >
                Change Password
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {!showPasswordForm ? (
            <div className="bg-slate-50 rounded-2xl p-5 border border-outline-variant/20">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="text-sm font-bold text-slate-700">Password</p>
                  <p className="text-xs text-slate-500 font-medium mt-0.5">Last changed: Unknown</p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Current Password</label>
                <input
                  type="password"
                  value={passwords.currentPassword}
                  onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="Enter current password"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">New Password</label>
                <input
                  type="password"
                  value={passwords.newPassword}
                  onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="Min 6 characters"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Confirm Password</label>
                <input
                  type="password"
                  value={passwords.confirmPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="Re-enter new password"
                  required
                  minLength={6}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="flex-1 flex items-center justify-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-primary shadow-sm hover:shadow-primary/25 transition-all duration-300 active:scale-95 disabled:opacity-50"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="px-5 py-3 bg-slate-50 text-slate-600 rounded-xl font-bold text-sm border border-outline-variant/20 hover:bg-white hover:shadow-sm transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
